import React, { useState, useEffect } from "react";
import FaqSection from "../components/FaqSection";
import { Helmet } from "react-helmet-async";
import zohoStyles from "./ZohoFinal.module.css"; // ✅ Using Zoho CSS
import Navigation from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Plus,
  Minus,
  Phone,
  Mail,
  ArrowRight,
  Building2,
  Shield,
  PlugZap,
  ServerCog,
  BarChart3,
  LayoutDashboard,
  Workflow,
  Globe,
} from "lucide-react";

/* ✅ Import images */
import NetSuiteLogo from "../assests/images/NetSuitee.jpg";
import hackerRankLogo from "../assests/images/HackerRank.webp";
import GrandeeLogo from "../assests/images/Grandee.png";
import jaggaerLogo from "../assests/images/jaggaer.webp";

const siteUrl = "https://www.cloudare.in/services-netsuite";
const phoneDisplay = "+1 628-282-5683";
const phoneHref = "+16282825683";
const email = "sales@cloudare.in";
const calendly = "https://calendly.com/cloudare";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Cloudare Technologies",
  url: "https://www.cloudare.in",
  logo: "https://www.cloudare.in/logo192.png",
  sameAs: [],
};

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Oracle NetSuite Implementation & Support",
  description:
    "Cloudare delivers end-to-end Oracle NetSuite services: implementations, integrations, Budget NetSuite Support, India GST e-invoicing/E-Way Bill, Malaysia LHDN e-invoicing, billing & revenue, and managed services across Central India and globally.",
  provider: {
    "@type": "Organization",
    name: "Cloudare Technologies",
    url: "https://www.cloudare.in",
  },
  areaServed: [
    "Raipur (Chhattisgarh, India)",
    "Indore (Madhya Pradesh, India)",
    "Bhubaneswar (Odisha, India)",
    "Nagpur (Maharashtra, India)",
    "Central India",
    "Kingdom of Saudi Arabia (KSA)",
    "Malaysia",
    "United States",
    "Canada",
  ],
};

const faqList = [
  {
    q: "Do you implement NetSuite GST e-invoicing (IRP) and E-Way Bill in India?",
    a: "Yes. We deliver end-to-end IRP connectivity, generation, cancellations, reconciliations, and dashboards directly within NetSuite.",
  },
  {
    q: "Do you support Malaysia LHDN e-invoicing from NetSuite?",
    a: "Yes. Compliant issuance, status callbacks, cancellations, archiving, and monitoring dashboards in NetSuite.",
  },
  {
    q: "Can you integrate Salesforce with NetSuite for lead-to-cash?",
    a: "Yes. Two-way sync of opportunities/orders, items, pricebooks, approvals, and exceptions using SuiteTalk/REST or iPaaS.",
  },
  {
    q: "Do you offer Budget NetSuite Support plans?",
    a: "Yes. Flexible ticket packs or monthly hours with SLAs, release support, and admin training for SMBs in Central India.",
  },
  {
    q: "Do you serve Raipur, Chhattisgarh (CG), and Odisha?",
    a: "Yes. We focus on Central India with remote and on-site delivery for Raipur, CG, and Odisha.",
  },
  {
    q: "What is a typical NetSuite go-live timeline?",
    a: "For SMB rollouts we target 8–16 weeks depending on scope, data readiness, and integrations.",
  },
  {
    q: "Can you migrate from Tally/QuickBooks/SAP Business One to NetSuite?",
    a: "Yes. We handle extraction, transformation, opening balances, historical transactions, and validation cycles.",
  },
  {
    q: "Do you implement NetSuite Billing & Revenue (RevRec/ASC-606)?",
    a: "Yes. Subscriptions, usage billing, revenue recognition, and audit-ready reporting can be configured.",
  },
  {
    q: "Can you build custom SuiteScripts and SuiteFlows?",
    a: "Yes. We develop and QA custom scripts, workflows, roles/permissions, and scheduled automations with best practices.",
  },
  {
    q: "Do you manage NetSuite release upgrades?",
    a: "Yes. Sandbox testing, change logs, script/flow updates, and deployment coordination are part of managed services.",
  },
  {
    q: "Which industries do you serve with NetSuite?",
    a: "Technology/SaaS, manufacturing, distribution, retail/eCommerce, services, and education.",
  },
  {
    q: "Do you integrate payment gateways and logistics providers?",
    a: "Yes. We integrate payments, 3PL/shipments, taxation, and other third-party platforms via REST/SuiteTalk or iPaaS.",
  },
  {
    q: "How do you ensure data security and access control?",
    a: "Roles, permissions, segregation of duties, audit trails, and least-privilege access are configured and reviewed.",
  },
  {
    q: "Do you provide training and documentation?",
    a: "Yes. Role-based training, SOPs, handover, and recorded sessions.",
  },
  {
    q: "Do you provide NetSuite services for businesses in Tier-2 cities?",
    a: "Yes — we support SMEs and enterprises in Tier-2 cities like Raipur, Indore, Bhubaneswar, Nagpur, and Jaipur with complete NetSuite implementation, GST compliance, and managed support.",
  },
];

export default function OracleNetSuitePage() {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <main className={zohoStyles.main}>
      <Navigation />
      <Helmet>
        <title>
          Oracle NetSuite Partner in Raipur (CG) — Implementation & Support |
          Cloudare
        </title>
        <meta
          name="description"
          content="Cloudare — Oracle NetSuite Partner in Central India (Raipur, Chhattisgarh). NetSuite Implementation, GST e-invoicing, Salesforce integration, Budget Support across India & globally."
        />
        <meta
          name="keywords"
          content="NetSuite Raipur, NetSuite CG, NetSuite Chhattisgarh, NetSuite Odisha, NetSuite Partner India, NetSuite GST, NetSuite Support, Budget NetSuite"
        />
        <link rel="canonical" href={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(serviceJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqList.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className={zohoStyles.hero}>
        <div className={zohoStyles.containerGrid}>
          <div className={zohoStyles.headContainer}>
            <img
              src={NetSuiteLogo}
              alt="Oracle NetSuite logo"
              className={zohoStyles.heroLogo}
            />
            <h1 className={zohoStyles.heroTitle1}>
              Oracle NetSuite Implementation & Support — India & Beyond
            </h1>
            <p className={zohoStyles.heroDesc}>
              We deliver end-to-end Oracle NetSuite services—including
              implementations, integrations, Budget Support, India GST
              e-invoicing/E-Way Bill, Malaysia LHDN e-invoicing, billing &
              revenue, and managed services—for businesses across Central India,
              KSA, Malaysia, the US, Canada, and other global markets.
            </p>

            <div className={zohoStyles.heroButtons}>
              <a
                href={calendly}
                target="_blank"
                rel="noopener noreferrer"
                className={zohoStyles.btnPrimary}
              >
                Book a Free Consult <ArrowRight className={zohoStyles.iconSm} />
              </a>
              <a href="#faq" className={zohoStyles.btnSecondary}>
                View FAQs <ArrowRight className={zohoStyles.iconSm} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>
            NetSuite Services We Offer
          </h2>
          <div className={zohoStyles.appGrid}>
            <AppCard
              title="Implementation & Rollout"
              desc="Discovery, design, configuration, data migration, UAT, and go-live."
              icon={<Building2 className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Customization (SuiteScript/Flow)"
              desc="Custom objects, scripts, workflows, roles & permissions with QA."
              icon={<ServerCog className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Integrations (SuiteTalk/REST)"
              desc="Salesforce, payment gateways, logistics, tax & e-invoicing platforms."
              icon={<PlugZap className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Finance & India GST"
              desc="E-Invoicing (IRP), E-Way Bill, reconciliations, audits & dashboards."
              icon={<Shield className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Billing & Revenue"
              desc="Subscriptions, usage billing, revenue recognition (ASC-606) & compliance."
              icon={<BarChart3 className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Managed Services (Budget)"
              desc="Ticket packs or monthly hours with SLAs, release support & training."
              icon={<Workflow className={zohoStyles.iconLg} />}
            />
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className={zohoStyles.sectionAlt}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>
            Why Choose Oracle NetSuite with Cloudare
          </h2>
          <div className={zohoStyles.featureGrid}>
            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <Building2 className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>Unified Cloud ERP</h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                Financials, order management, inventory, projects & billing —
                all in one scalable platform.
              </p>
            </div>

            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <Shield className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>
                  Global + Local Compliance
                </h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                Multi-subsidiary, multi-currency, India GST e-invoicing, E-Way
                Bill & regional tax packs.
              </p>
            </div>

            <div className={zohoStyles.featureCard}>
              <div className={zohoStyles.featureHeader}>
                <div className={zohoStyles.featureIcon}>
                  <PlugZap className={zohoStyles.iconLg} />
                </div>
                <h3 className={zohoStyles.featureTitle}>Automate & Control</h3>
              </div>
              <p className={zohoStyles.featureDesc}>
                SuiteFlow approvals, roles, audits, SoD & compliance with
                real-time analytics dashboards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className={zohoStyles.sectionAlt}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>Success Stories</h2>
          <p className={zohoStyles.sectionDesc}>
            Selected NetSuite deployments and outcomes we delivered.
          </p>
          <div className={zohoStyles.caseGrid}>
            <CaseStudy
              logo={hackerRankLogo}
              alt="HackerRank logo"
              title="HackerRank — NetSuite GST E-Invoicing (IRP)"
              bullets={[
                "Direct e-invoice generation with IRP integration",
                "Automated reconciliation & 100% accuracy",
              ]}
            />
            <CaseStudy
              logo={jaggaerLogo}
              alt="AMDT / Jaggaer logo"
              title="AMDT — Salesforce → NetSuite Order Automation"
              bullets={[
                "Lead-to-Cash sync via SuiteTalk/REST",
                "Automated item, pricebook & order creation",
              ]}
            />
            <CaseStudy
              logo={GrandeeLogo}
              alt="Grandee Marketing logo"
              title="Grandee — Malaysia LHDN E-Invoicing"
              bullets={[
                "Compliant issuance & callbacks",
                "Archiving & audit logs for authorities",
              ]}
            />
          </div>
          <div className={zohoStyles.statGrid}>
            <StatCard
              label="Successful Implementations"
              value="25+"
              maxProgress={99}
            />
            <StatCard
              label="Average IRN Accuracy"
              value="99.8%"
              maxProgress={95}
            />
            <StatCard
              label="Typical Go-Live"
              value="8-16 wks"
              maxProgress={95}
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>What Our Clients Say</h2>
          <div className={zohoStyles.grid3}>
            <Testimonial
              name="Tech Lead, HackerRank"
              text="Cloudare's GST e-invoicing implementation was seamless. 100% accuracy from day one."
            />
            <Testimonial
              name="Operations Head, AMDT"
              text="Salesforce to NetSuite automation reduced manual work by 70%. Excellent execution."
            />
            <Testimonial
              name="Finance Manager, Grandee"
              text="LHDN compliance was handled expertly. Great support and documentation."
            />
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact" className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <div className={zohoStyles.contactCard}>
            <div className={zohoStyles.contact1}>
              <h2 className={zohoStyles.sectionTitle}>
                Talk to a NetSuite Expert
              </h2>
              <p className={zohoStyles.heroDesc}>Book a free 30-min Call.</p>
              <div className={zohoStyles.heroButtons}>
                {/* <a href={calendly} className={zohoStyles.btnPrimary}>
                  Book on Calendly <ArrowRight className={zohoStyles.iconSm} />
                </a> */}
                <a
                  href={calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={zohoStyles.btnPrimary}
                >
                  Book on Calendly <ArrowRight className={zohoStyles.iconSm} />
                </a>
                <a href={`mailto:${email}`} className={zohoStyles.btnSecondary}>
                  <Mail className={zohoStyles.iconSm} /> {email}
                </a>
              </div>
            </div>
            <div className={zohoStyles.contactInfo}>
              <ContactRow
                label="Phone"
                value={phoneDisplay}
                href={`tel:${phoneHref}`}
                icon={<Phone className={zohoStyles.iconSm} />}
              />
            </div>
          </div>
        </div>
      </section>

      <div id="faq">
        <FaqSection
          title="Oracle NetSuite — FAQ"
          faqs={faqList.map((x) => ({ question: x.q, answer: x.a }))}
          emitJsonLd
        />
      </div>

      <Footer />
    </main>
  );
}

/* ✅ Subcomponents (same pattern as Zoho page) */

function AppCard({ title, desc, icon }) {
  return (
    <div className={zohoStyles.appCard}>
      <div className={zohoStyles.appHeader}>
        <div className={zohoStyles.appIcon}>{icon}</div>
        <h3 className={zohoStyles.appTitle}>{title}</h3>
      </div>
      <p className={zohoStyles.appDesc}>{desc}</p>
    </div>
  );
}

function CaseStudy({ logo, alt, title, bullets }) {
  return (
    <div className={zohoStyles.caseCard}>
      <div className={zohoStyles.caseImage}>
        <img src={logo} alt={alt} className={zohoStyles.caseLogo} />
      </div>
      <div>
        <h3 className={zohoStyles.caseTitle}>{title}</h3>
        <ul className={zohoStyles.caseList}>
          {bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StatCard({ label, value, maxProgress = 70 }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1200;
    const stepTime = 20;
    const step = Math.ceil(maxProgress / (duration / stepTime));

    const interval = setInterval(() => {
      start += step;
      if (start >= maxProgress) {
        start = maxProgress;
        clearInterval(interval);
      }
      setProgress(start);
    }, stepTime);

    return () => clearInterval(interval);
  }, [maxProgress]);

  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className={zohoStyles.statCard}>
      <svg
        className={zohoStyles.circleChart}
        width="100"
        height="100"
        viewBox="0 0 100 100"
      >
        <circle
          className={zohoStyles.circleBg}
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="10"
          fill="none"
        />
        <circle
          className={zohoStyles.circleProgress}
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="10"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="middle"
          className="statValue"
        >
          {value}
        </text>
      </svg>
      <div className="statLabel">{label}</div>
    </div>
  );
}

function Testimonial({ name, text }) {
  return (
    <div className={zohoStyles.proCard}>
      <div className={zohoStyles.testimonialBox}>
        <div className={zohoStyles.avatar}></div>
        <div>
          <div className={zohoStyles.testimonialName}>{name}</div>
          <div className={zohoStyles.testimonialText}>{text}</div>
        </div>
      </div>
    </div>
  );
}

function ContactRow({ label, value, href, icon }) {
  return (
    <div className={zohoStyles.contactRow}>
      <div className={zohoStyles.contactIcon}>{icon}</div>
      <div>
        <div className={zohoStyles.contactLabel}>{label}</div>
        <a href={href} className={zohoStyles.contactValue}>
          {value}
        </a>
      </div>
    </div>
  );
}
